/**
 * Shared feature flag definitions used by both the frontend and backend.
 * Defines canonical feature flag IDs, metadata, and pure lookup helpers with no framework/runtime coupling.
 * Update whenever feature flags are added/removed, their IDs/metadata change, or categories are revised.
 */

import { ROLES } from './roles.js';
import type { RoleId } from './roles.js';

export interface FeatureFlagDefinition {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly defaultEnabled: boolean;
  /**
   * Role whitelist. Omit for a role-agnostic flag that can be granted to
   * any user via per-user override (pure user-specific gating).
   */
  readonly roles?: readonly RoleId[];
  readonly category: 'content' | 'admin';
}

const ADMIN_FEATURE_MANAGEMENT = Object.freeze({
  id: 'admin_feature_management',
  name: 'Admin Feature Management',
  description: 'Administrative interface for managing feature flags',
  defaultEnabled: false,
  roles: Object.freeze([ROLES.SUPER_ADMIN, ROLES.ADMIN]),
  category: 'admin',
} as const satisfies FeatureFlagDefinition);

const NONPROFIT_COHORT_MANAGEMENT = Object.freeze({
  id: 'nonprofit_cohort_management',
  name: 'Nonprofit Cohort Management Delegation',
  description:
    'Per-user delegation of the nonprofit administration surface through CAPABILITY_DELEGATIONS; this is an access grant, not a rollout.',
  defaultEnabled: false,
  roles: Object.freeze([ROLES.SUPER_ADMIN, ROLES.TEACHER]),
  category: 'admin',
} as const satisfies FeatureFlagDefinition);

const NONPROFIT_DEMO_VIEW = Object.freeze({
  id: 'nonprofit_demo_view',
  name: 'Nonprofit Demo Student View',
  description:
    'Lets an active nonprofit teacher use their own sample student in a private demo group. Set up or disable access in Organization → People → Sample student. Setup creates the private group and enables this grant atomically; enabling the flag alone is insufficient. Access is checked on every student-view request.',
  defaultEnabled: false,
  roles: Object.freeze([ROLES.TEACHER]),
  category: 'admin',
} as const satisfies FeatureFlagDefinition);

/**
 * Available feature flags in the system.
 */
export const FEATURE_FLAGS = Object.freeze({
  ADMIN_FEATURE_MANAGEMENT,
  NONPROFIT_COHORT_MANAGEMENT,
  NONPROFIT_DEMO_VIEW,
} as const);

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];
export type FeatureFlagId = FeatureFlag['id'];
export type FeatureCategoryId = FeatureFlagDefinition['category'];

/**
 * Feature flag categories for organization.
 */
export const FEATURE_CATEGORIES = Object.freeze({
  content: 'Content Management',
  admin: 'Administration',
} as const satisfies Record<FeatureCategoryId, string>);

const FEATURE_FLAG_LIST: readonly FeatureFlag[] = Object.freeze(
  Object.values(FEATURE_FLAGS)
);

const FEATURE_FLAG_BY_ID: Readonly<Record<FeatureFlagId, FeatureFlag>> =
  Object.freeze(
    FEATURE_FLAG_LIST.reduce(
      (acc, featureFlag) => {
        acc[featureFlag.id] = featureFlag;
        return acc;
      },
      {} as Record<FeatureFlagId, FeatureFlag>
    )
  );

const featureFlagsByCategoryDraft = FEATURE_FLAG_LIST.reduce(
  (acc, featureFlag) => {
    acc[featureFlag.category].push(featureFlag);
    return acc;
  },
  {
    content: [] as FeatureFlag[],
    admin: [] as FeatureFlag[],
  }
);

const FEATURE_FLAGS_BY_CATEGORY: Readonly<
  Record<FeatureCategoryId, readonly FeatureFlag[]>
> = Object.freeze({
  content: Object.freeze(featureFlagsByCategoryDraft.content),
  admin: Object.freeze(featureFlagsByCategoryDraft.admin),
});

const EMPTY_FEATURE_FLAG_LIST: readonly FeatureFlag[] = Object.freeze([]);

/**
 * Get all feature flags as an array.
 */
export const getAllFeatureFlags = (): readonly FeatureFlag[] => {
  return FEATURE_FLAG_LIST;
};

/**
 * Get feature flags by category.
 */
export const getFeatureFlagsByCategory = (
  category: string
): readonly FeatureFlag[] => {
  if (!category) return EMPTY_FEATURE_FLAG_LIST;

  return (
    FEATURE_FLAGS_BY_CATEGORY[category as FeatureCategoryId] ??
    EMPTY_FEATURE_FLAG_LIST
  );
};

/**
 * Get feature flag by ID.
 */
export const getFeatureFlag = (featureId: string): FeatureFlag | undefined => {
  if (!featureId) return undefined;

  return FEATURE_FLAG_BY_ID[featureId as FeatureFlagId];
};

/**
 * Check if a feature flag exists.
 */
export const isValidFeatureFlag = (featureId: string): boolean => {
  if (!featureId) return false;

  return Object.hasOwn(FEATURE_FLAG_BY_ID, featureId);
};
